/**
 * The transient message stack.
 *
 * Lifted out of the app shell when toasts grew a leaving state and a mark per
 * kind — the shell should mount the stack, not describe what a toast looks like.
 *
 * A TOAST IS NOT A DIALOG. It never takes focus and never blocks the page: the
 * stack is pointer-transparent and only the cards themselves take pointer events,
 * so a toast sitting over the bottom nav does not swallow taps meant for the tab
 * underneath it. It announces itself instead — polite for a confirmation,
 * assertive for an error, which is the one case worth interrupting for.
 *
 * THE MARK CARRIES THE TONE, not the colour alone. Success, info and error are
 * three different shapes, so the kind survives a greyscale screenshot and a
 * red-green colour deficiency.
 *
 * LEAVING IS A STATE, NOT A DELETION. The store keeps a dismissed toast in the
 * list with `exiting` set for one animation before removing it (see dismissToast
 * in store/store.ts); without that the card would unmount on the same frame it
 * was dismissed and there would be nothing left to animate out. The two
 * durations — TOAST_EXIT_MS there and the toast-out animation in the stylesheet —
 * are a pair and have to move together.
 */

import { dismissToast } from '@/store/store';
import { useSlice } from '@/store/hooks';
import { useT } from '@/i18n';
import type { Toast } from '@/lib/types';

/** The per-kind mark. Decorative: the message beside it carries the meaning. */
function ToastMark({ kind }: { kind: Toast['kind'] }) {
  const paths: Record<Toast['kind'], string[]> = {
    // A tick, for something that completed.
    success: ['M20 6 9 17l-5-5'],
    // An i, for something that merely happened.
    info: ['M12 16v-5', 'M12 8v.01', 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z'],
    // A bang, for something that did not work.
    error: ['M12 8v5', 'M12 16.5v.01', 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z'],
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths[kind].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/** The close glyph. Drawn rather than a &times; so it matches the app's line weight. */
function CloseGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

/** Render the stack of live toasts. */
export default function Toaster() {
  const ui = useSlice('ui');
  const t = useT();
  if (ui.toasts.length === 0) return null;
  return (
    <div className="toast-stack">
      {ui.toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast toast--${toast.kind}`}
          data-state={toast.exiting ? 'leaving' : 'entering'}
          role={toast.kind === 'error' ? 'alert' : 'status'}
          aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <span className="toast__mark" aria-hidden="true">
            <ToastMark kind={toast.kind} />
          </span>
          <span className="toast__message">{toast.text}</span>
          <button
            type="button"
            className="toast__close"
            aria-label={t('common.close')}
            onClick={() => dismissToast(toast.id)}
          >
            <CloseGlyph />
          </button>
        </div>
      ))}
    </div>
  );
}
